<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { VDataTable, VCheckboxBtn } from 'vuetify/components';
import { useGetOrganizationGroups, CustomerMgmtTypes } from '@ilteducation/customer-mgmt';
import {
  checkMembershipDeletePermission,
  checkMembershipCreatePermission,
} from '@ilteducation/customer-mgmt-permissions';
import { ScrollingDataTableServer } from '@ilteducation/components';
import PermissionLock from '../../PermissionLock.vue';
import type { FetchItems } from '@ilteducation/components';
import { useLocale } from '../../../composables/useLocale';
import GroupLabel from '../../GroupLabel.vue';
import { usePermissions } from '../../../composables/usePermissions';

export type SortOrder = 'asc' | 'desc';

type Props = {
  organizationUid: string;
  search: string | undefined;
  user: CustomerMgmtTypes.GetUserResponse;
  memberships: CustomerMgmtTypes.MembershipOutput[];
};
const props = defineProps<Props>();

const { t } = useLocale();
const { loadGroups } = useGetOrganizationGroups();
const { permissionContext, getCallerAssociation, createGetTargetAssociation, getGroupMemberships, reporterWithReason } =
  usePermissions();

const table = ref<typeof ScrollingDataTableServer | null>(null);

const membershipByGroupUid = computed(() =>
  Object.fromEntries(props.memberships.map((membership) => [membership.group.uid, membership])),
);

const getTargetAssociation = computed(() => createGetTargetAssociation(props.user));

const permissionByGroupUid = ref<Record<string, ReturnType<typeof reporterWithReason>>>({});

// Recompute permissions when the target user / organization changes.
watch(
  () => [props.user, props.organizationUid],
  () => {
    permissionByGroupUid.value = {};
  },
);

const evaluateGroupPermission = async (group: CustomerMgmtTypes.GroupOutput) => {
  if (permissionByGroupUid.value[group.uid]) {
    return;
  }

  const membership = membershipByGroupUid.value[group.uid];
  const result = membership
    ? await checkMembershipDeletePermission(
        permissionContext.value,
        {
          ...membership,
          group: {
            ...membership.group,
            organization: membership.group.schoolUnit,
          },
        },
        getCallerAssociation,
        getTargetAssociation.value,
        getGroupMemberships,
        reporterWithReason,
      )
    : await checkMembershipCreatePermission(
        permissionContext.value,
        { ...group, organization: group.schoolUnit },
        { uid: props.user.uid },
        getCallerAssociation,
        getTargetAssociation.value,
        getGroupMemberships,
        reporterWithReason,
      );

  permissionByGroupUid.value = { ...permissionByGroupUid.value, [group.uid]: result };
};

const fetchItems: FetchItems<typeof loadGroups> = async (args) => {
  const { pageNumber, pageSize, sortBy, sortOrder, q } = args;
  const response = await loadGroups(props.organizationUid, pageNumber, pageSize, sortBy, sortOrder, q);
  await Promise.all((response?.data ?? []).map(evaluateGroupPermission));
  return response;
};

const filters = computed(() => ({ organization: props.organizationUid }));

const headers = computed((): VDataTable['$props']['headers'] => [
  // max width set to view width, percent dont work :(
  { title: t('lcp.group_name'), key: 'name', align: 'start', sortable: true, maxWidth: '20vw', nowrap: true },
  { title: t('lcp.members'), key: 'memberCount', align: 'start', sortable: false, maxWidth: '10vw', nowrap: true },
]);

const model = defineModel<string[]>();

// Named after the row it selects: on its own the checkbox is one of a column of identical boxes.
const selectGroupLabel = (group: CustomerMgmtTypes.GroupOutput) => `${t('lcp.select')}: ${group.name}`;

const pageUpdated = () => {};
const sortingUpdated = () => {};
const onItemSelection = () => {};
</script>
<template>
  <ScrollingDataTableServer
    ref="table"
    :itemsPerPage="100"
    list="groups"
    item-value="uid"
    :fetchItems="fetchItems"
    :filters="filters"
    :search-string="search"
    sort-by="name"
    sort-order="asc"
    :headers="headers"
    v-model="model"
    :show-select="true"
    @pageUpdated="pageUpdated"
    @sortingUpdated="sortingUpdated"
    @item-selection="onItemSelection"
  >
    <!-- No select-all box in this column, and an empty header cell leaves every checkbox under it
    without a column to belong to. The heading is for assistive technology only, so the column still
    reads as bare as it looks. -->
    <template v-slot:[`header.data-table-select`]>
      <span class="d-sr-only">{{ t('lcp.select') }}</span>
    </template>
    <template #[`item.name`]="{ item }">
      <GroupLabel :name="item.name" :years-in-school="item.yearsInSchool" />
    </template>
    <template #[`item.data-table-select`]="{ item, internalItem, isSelected, toggleSelect }">
      <v-checkbox-btn
        v-if="permissionByGroupUid[item.uid]?.allowed"
        :model-value="isSelected(internalItem)"
        :aria-label="selectGroupLabel(item)"
        @update:model-value="toggleSelect(internalItem)"
      ></v-checkbox-btn>
      <div v-else class="d-flex align-center justify-center" style="width: 40px; height: 40px">
        <PermissionLock :reason="permissionByGroupUid[item.uid]?.reason ?? 'not_permitted'" color="primary" />
      </div>
    </template>
  </ScrollingDataTableServer>
</template>
